import { useEffect, useState } from "react";
import type { Ordre } from "types/ordre";
import type { Vehicle, VehicleReservation } from "types/garage/vehicules";
import { vehiclesApi } from "@services/api/vehicles";
import { ordresApi } from "@services/api/ordres";
import { useDataStore } from "@store/user_org";
import { useOrdreStore } from "@store/ordreStore";
import { showNotification } from "context/notification.service";
import ReservationModal from "@components/modals/ReservationModal";

/**
 * Opens the flotte "Modifier la réservation" modal for one of the ordre's mobilités.
 * Fetches the reservation + fleet on mount, then reuses ReservationModal so the
 * loaner-desk and the ordre actions menu edit the same way.
 *
 * `reservationId` désigne LAQUELLE : un ordre peut enchaîner un prêt puis une location,
 * et `ordre.rental_reservation_id` ne reflète que celle du moment. Le miroir porté par
 * l'ordre est recalculé côté back après chaque écriture — on se contente de relire
 * l'ordre, au lieu de l'épingler sur la réservation qu'on vient de toucher.
 */
export default function EditRentalReservationModal({
  ordre,
  reservationId,
  onClose,
  onChanged,
}: {
  ordre: Ordre;
  reservationId?: string;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const orgId = useDataStore((s) => s.orgData?.id);
  const [reservation, setReservation] = useState<VehicleReservation | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);

  const resId = reservationId || ordre.rental_reservation_id;

  useEffect(() => {
    if (!resId) { onClose(); return; }
    let alive = true;
    Promise.all([
      vehiclesApi.getReservation(resId),
      orgId ? vehiclesApi.list({ org_id: orgId, limit: 500 }) : Promise.resolve([]),
    ])
      .then(([res, list]) => {
        if (!alive) return;
        setReservation(res);
        setVehicles(list);
      })
      .catch(() => {
        if (!alive) return;
        showNotification({ type: "error", message: "Réservation introuvable" });
        onClose();
      });
    return () => { alive = false; };
  }, [resId, orgId]);

  const refreshOrdre = async () => {
    try {
      useOrdreStore.getState().updateOrdre(await ordresApi.get(ordre.id));
    } catch {
      // Le miroir est déjà écrit côté serveur : une relecture ratée n'annule rien.
    }
  };

  if (!reservation) return null;

  return (
    <ReservationModal
      reservation={reservation}
      vehicles={vehicles}
      onClose={onClose}
      onSave={async (updated) => {
        try {
          await vehiclesApi.updateReservation(reservation.id, updated);
          await refreshOrdre();
          showNotification({ type: "success", message: "Réservation modifiée" });
          onChanged?.();
        } catch (e: any) {
          showNotification({
            type: "error",
            message: e?.response?.status === 409
              ? e.response.data?.detail || "Une autre mobilité occupe déjà cette période."
              : "Erreur lors de la modification",
          });
        }
      }}
      onSwapped={async () => {
        await refreshOrdre();
        onChanged?.();
      }}
      onDelete={async (id) => {
        try {
          await vehiclesApi.deleteReservation(id);
          await refreshOrdre();
          showNotification({ type: "success", message: "Mobilité retirée" });
          onChanged?.();
        } catch {
          showNotification({ type: "error", message: "Erreur" });
        }
      }}
    />
  );
}
